<template>
    <div class="customer-profile">
        <main>
            <DToaster
                v-if="axiosMessages.showMessage"
                placement="top-end"
                class="z-index-1000"
                visible
            >
                <DToast
                    :key="'flash'"
                    :color="axiosMessages.success ? 'success' : 'danger'"
                    :visible="true"
                    :header-text=" axiosMessages.success ? __('messages.success') : __('messages.warning')"
                    :body-text="axiosMessages.message"
                    @close="resetAxiosMessage"
                />
            </DToaster>
            <DCard padding>
                <div class="detail">
                    <div :class="{'has-incident' : customer.has_incidents}">
                        <span>{{ __('customers.customers.brand') }}</span>
                        <span v-if="customer.brand">{{ customer.brand.name }}</span>
                        <DTag
                            v-else
                            :label="__('messages.not_set')"
                        />
                        <IncidentFlag
                            v-if="userPermissions.includes('customer.incidents_view') && customer.has_incidents"
                            id="customer-incident-flag"
                            :active-incident="customer.has_open_incidents"
                            :no-link="true"
                        />
                    </div>
                    <div>
                        <span>{{ __('customers.customers.customer_segments') }}</span>
                        <div class="segments">
                            <DTag
                                v-for="segment in segments"
                                :key="segment"
                                :label="segment"
                            />
                            <DTag
                                v-if="customer.is_reseller"
                                :label="__('customers.customers.is_reseller')"
                            />
                            <DTag
                                v-if="segments.length === 0 && !customer.is_reseller"
                                :label="__('messages.not_set')"
                            />
                        </div>
                    </div>
                    <div>
                        <span>{{ __('customers.customers.customer_type') }}</span>
                        <span v-if="customer.customer_type">{{ __(`customers.customers.customer_types.${customer.customer_type}`) }}</span>
                        <DTag
                            v-else
                            :label="__('messages.not_set')"
                        />
                    </div>
                    <div>
                        <span>{{ __('customers.customers.customer_language') }}</span>
                        <span v-if="customer.customer_language">
                            {{ customer.customer_language }}
                        </span>
                        <DTag
                            v-else
                            :label="__('messages.not_set')"
                        />
                    </div>
                    <div v-if="customer.legal_form">
                        <span>{{ __('customers.common.legal_form') }}</span>
                        <span>{{ customer.legal_form }}</span>
                    </div>
                    <div>
                        <span>{{ __('customers.customers.created') }}</span>
                        <span v-if="customer.created_at">{{ setDateFormat(customer.created_at) }}</span>
                        <DTag
                            v-else
                            :label="__('messages.unknown')"
                        />
                    </div>
                    <div>
                        <span>{{ __('customers.customers.brand_customer_number') }}</span>
                        <span v-if="customer.brand_customer_number">{{ customer.brand_customer_number }}</span>
                        <DTag
                            v-else
                            :label="__('messages.not_set')"
                        />
                    </div>
                    <div
                        v-if="customer.customer_type === 'geschäftlich' && $page.props.tenantSettings?.enable_commercial_register_number === true"
                    >
                        <span>{{ __('customers.customers.commercial_register_number') }}</span>
                        <span v-if="customer.commercial_register_number">{{ customer.commercial_register_number }}</span>
                        <DTag
                            v-else
                            :label="__('messages.not_set')"
                        />
                    </div>
                    <div v-if="useHeroldLocationIdOnCustomer">
                        <span>{{ __('customers.customers.herold_location_id') }}</span>
                        <span v-if="customer.herold_location_name">{{ customer.herold_location_name }}</span>
                        <DTag
                            v-else
                            :label="__('messages.not_set')"
                        />
                    </div>
                    <div>
                        <span>{{ __('customers.customers.cloudpit_account_id') }}</span>
                        <div class="d-flex">
                            <span v-if="customer.cloudpit_account_id">{{ customer.cloudpit_account_id }}</span>
                            <DTag
                                v-else-if="isOneHomeAccountCreationPending"
                                :label="__('messages.pending')"
                            />
                            <DTag
                                v-else
                                :label="__('messages.not_set')"
                            />
                            <DButtonDropdown
                                v-if="customer.cloudpit_account_id"
                                icon="dots-horizontal"
                                class="action-buttons"
                            >
                                <DDropdownItem
                                    v-if="customer.cloudpit_account_id"
                                    v-c-tooltip="__('customers.customers.cloudpit_password_reset_hint')"
                                    @click="showPasswordResetModal=true"
                                >
                                    {{ __('customers.customers.cloudpit_password_reset') }}
                                </DDropdownItem>
                                <DDropdownItem
                                    v-if="customer.cloudpit_account_id"
                                    v-c-tooltip="__('customers.customers.cloudpit_disable_2fa_hint')"
                                    @click="showDisable2FAModal=true"
                                >
                                    {{ __('customers.customers.cloudpit_disable_2fa') }}
                                </DDropdownItem>
                                <DDropdownItem
                                    v-if="customer.cloudpit_account_id && userPermissions.includes('customer.view')"
                                    v-c-tooltip="__('customers.customers.cloudpit_onetime_login_hint')"
                                    @click="getCloudPitUsersForTheAccount"
                                >
                                    {{ __('customers.customers.cloudpit_onetime_login') }}
                                </DDropdownItem>
                                <DDropdownItem
                                    v-if="customer.cloudpit_account_id"
                                    v-c-tooltip="__('customers.customers.switch_panel')"
                                    @click="showSwitchPanelModal=true"
                                >
                                    {{ __('customers.customers.switch_panel_hint') }}
                                </DDropdownItem>
                                <DDropdownItem
                                    v-if="userPermissions.includes('customer.edit')"
                                    @click="$inertia.get($route('customers.customers.cloudpit_sync', customer.id))"
                                >
                                    {{ __('messages.actions.cloudpit_sync') }}
                                </DDropdownItem>
                                <DDropdownItem
                                    v-if="userPermissions.includes('customer.edit')"
                                    @click="$inertia.post($route('customers.customers.sync_one_home', customer.id))"
                                >
                                    {{ __('messages.actions.one_home_customer_sync') }}
                                </DDropdownItem>
                            </DButtonDropdown>
                        </div>
                    </div>
                    <div>
                        <span>{{ __('customers.customers.migration_date') }}</span>
                        <span v-if="customer.migrated_at">{{ setDateFormat(customer.migrated_at) }}</span>
                        <DTag
                            v-else
                            :label="__('messages.not_set')"
                        />
                    </div>
                    <div>
                        <span>{{ __('customers.customers.original_brand') }}</span>
                        <span v-if="customer.originalBrandName">{{ customer.originalBrandName }}</span>
                        <DTag
                            v-else
                            :label="__('messages.not_set')"
                        />
                    </div>
                    <div>
                        <span>{{ __('customers.customers.market_region') }}</span>
                        <span v-if="customer.market_region_id">{{ customer.marketRegion }}</span>
                        <DTag
                            v-else
                            :label="__('messages.not_set')"
                        />
                    </div>
                </div>
            </DCard>

            <DCard padding>
                <div class="detail">
                    <div>
                        <span>{{ __('customers.customers.address') }}</span>
                        <span>
                            <span
                                v-if="showCompanyField(customer.extended_company)"
                                class="extended-company"
                            >
                                {{ customer.extended_company }}<br v-if="customer.extended_company">
                            </span>
                            {{ customer.preceding_title }} {{ customer.firstname }} {{ customer.lastname }} {{ customer.following_title }}<br v-if="customer.firstname || customer.lastname">
                            {{ customer.address1 }}<br>
                            {{ customer.address2 }}<br v-if="customer.address2">
                            {{ customer.postal_code }} {{ customer.city }}<br>
                            <template v-if="customer.state">
                                {{ customer.state.name }}<br>
                            </template>
                            {{ customer.country ? customer.country.name : '' }}
                        </span>
                    </div>
                    <div>
                        <span>{{ __('customers.common.email') }}</span>
                        <div class="d-flex">
                            <span>
                                <a :href="`mailto:${customer.email}`">
                                    {{ customer.email }}
                                </a>
                            </span>
                            <DButton
                                mini
                                icon="content-copy"
                                class="copy-buttons"
                                @click="copyToClipboard(customer.email)"
                            />
                        </div>
                    </div>
                    <div>
                        <span>{{ __('customers.common.phone') }}</span>
                        <span>{{ customer.phone }}</span>
                    </div>
                    <div>
                        <span>{{ __('customers.common.mobilenumber') }}</span>
                        <span>{{ customer.mobilenumber }}</span>
                    </div>
                    <div>
                        <span>{{ __('customers.common.phone_pin') }}</span>
                        <span>{{ customer.phone_pin }}</span>
                    </div>
                    <div>
                        <span>{{ __('customers.customers.fax') }}</span>
                        <span>{{ customer.fax }}</span>
                    </div>
                </div>
            </DCard>

            <DCard
                v-if="customer.use_alternative_billing === true || customer.invoice_email !== null"
                padding
            >
                <div class="detail">
                    <div>
                        <span>{{ __('customers.customers.invoice_address') }}</span>
                        <span v-if="customer.use_alternative_billing === true">
                            {{ customer.extended_invoice_company }}<br v-if="customer.extended_invoice_company">
                            {{ customer.invoice_firstname }} {{ customer.invoice_lastname }}<br v-if="customer.invoice_firstname || customer.invoice_lastname">
                            {{ customer.invoice_address1 }}<br>
                            {{ customer.invoice_address2 }}<br v-if="customer.invoice_address2">
                            {{ customer.invoice_postal_code }} {{ customer.invoice_city }}<br>
                            <template v-if="customer.invoice_state">
                                {{ customer.invoice_state.name }}<br>
                            </template>
                            {{ customer.invoice_country ? customer.invoice_country.name : '' }}
                        </span>
                        <DTag
                            v-else
                            :label="__('customers.customers.no_alternative_billing_address')"
                        />
                    </div>
                    <div v-if="useHeroldLocationIdOnCustomer && customer.use_alternative_billing === true">
                        <span>{{ __('customers.customers.invoice_herold_location_id_billing') }}</span>
                        <span v-if="customer.invoice_herold_location_name_billing">
                            {{ customer.invoice_herold_location_name_billing }}
                        </span>
                        <DTag
                            v-else
                            :label="__('messages.not_set')"
                        />
                    </div>
                    <div>
                        <span>{{ __('customers.customers.invoice_email') }}</span>
                        <span>
                            <a :href="`mailto:${customer.invoice_email}`">
                                {{ customer.invoice_email }}
                            </a>
                        </span>
                    </div>
                    <div>
                        <span>{{ __('customers.customers.invoice_mobilenumber') }}</span>
                        <span>{{ customer.invoice_mobilenumber }}</span>
                    </div>
                    <div :class="{'vat-id-check' : customer.is_billing_vat_check_possible}">
                        <span>{{ __('customers.customers.invoice_vat_number') }}</span>
                        <span v-if="billingVatIdInformation.showVatNumber">
                            {{ customer.invoice_vat_number }}
                            <span
                                :class="billingVatIdInformation.iconClass"
                            /><br>
                            <span>
                                {{ billingVatIdInformation.message }}
                            </span>
                        </span>
                        <span v-if="billingVatIdInformation.isExempted">
                            {{ __('customers.customers.vat_number_exemption') }}
                        </span>
                        <DTag
                            v-if="billingVatIdInformation.isNotSet"
                            :label="__('customers.customers.no_vat_number')"
                        />
                        <span>
                            <DButton
                                v-if="customer.is_billing_vat_check_possible"
                                id="vat-id-check-billing"
                                mini
                                :label="__('customers.customers.vat_number_check')"
                                color="primary"
                                :disabled="!userPermissions.includes('customer.edit')"
                                @click="openCheckVatIdModal"
                            />
                        </span>
                    </div>
                </div>
            </DCard>

            <DCard padding>
                <div class="detail">
                    <div v-if="tenantSettings.enable_divergent_payment_methods === false">
                        <span>{{ __('customers.customers.payment_method') }}</span>
                        <span>{{ customer.payment_method }}</span>
                    </div>
                    <template v-if="creditCardPaymentMethod && customer.active_credit_card && tenantSettings.enable_divergent_payment_methods === false">
                        <div>
                            <span>{{ __('customers.customers.payment.credit_card.number') }}</span>
                            <span>{{ customer.active_credit_card.number.substr(-4, 4) }}</span>
                        </div>
                        <div>
                            <span>{{ __('customers.customers.payment.credit_card.expiry') }}</span>
                            <span>{{ customer.active_credit_card.expiry }}</span>
                        </div>
                        <div>
                            <span>{{ __('customers.customers.payment.credit_card.type') }}</span>
                            <span>{{ customer.active_credit_card.type }}</span>
                        </div>
                        <div>
                            <span>{{ __('customers.customers.payment.credit_card.owner') }}</span>
                            <span>{{ customer.active_credit_card.owner }}</span>
                        </div>
                    </template>
                    <template v-if="shouldShowSepa">
                        <div>
                            <span>{{ __('customers.customers.iban') }}</span>
                            <span>{{ customer.active_sepa_mandate.iban }}</span>
                        </div>
                        <div>
                            <span>{{ __('customers.customers.bic') }}</span>
                            <span>{{ customer.active_sepa_mandate.bic }}</span>
                        </div>
                        <div>
                            <span>{{ __('customers.customers.account_owner') }}</span>
                            <span>{{ customer.active_sepa_mandate.account_owner }}</span>
                        </div>
                        <div>
                            <span>{{ __('customers.customers.mandate_id') }}</span>
                            <span>{{ customer.active_sepa_mandate.id }}</span>
                        </div>
                        <div>
                            <span>{{ __('customers.customers.mandate_date') }}</span>
                            <span>{{ setDateFormat(customer.active_sepa_mandate.created_at) }}</span>
                        </div>
                    </template>
                    <div>
                        <span>{{ __('customers.customers.suppress_dunning_contract_termination') }}</span>
                        <span v-if="customer.suppress_dunning_contract_termination">
                            {{ __('messages.yes') }}
                        </span>
                        <span v-else>
                            {{ __('messages.no') }}
                        </span>
                    </div>
                    <div>
                        <span>{{ __('customers.customers.dunning_block_until') }}</span>
                        <DTag
                            v-if="customer.dunning_block_until && reminderStopUntilDateInPast"
                            :label="new Date(customer.dunning_block_until).toLocaleDateString()"
                        />
                        <DTag
                            v-else-if="customer.dunning_block_until"
                            color="danger"
                            :label="setDateFormat(customer.dunning_block_until)"
                        />
                        <DTag
                            v-else
                            :label="__('customers.customers.no_dunning_block')"
                        />
                    </div>
                    <div
                        v-if="tenantSettings.enable_divergent_payment_methods === false"
                    >
                        <span>{{ __('customers.customers.payment_term') }}</span>
                        <span>{{ customer.payment_term }} {{ __('customers.contracts.days') }}</span>
                    </div>
                    <div>
                        <span>{{ __('customers.customers.billing_type') }}</span>
                        <DTag
                            v-if="isInvoiceBeforeRenewalEnabled"
                            :label="__('customers.billing_types.invoice_renewal_enabled')"
                        />
                        <span v-else>{{ __('customers.billing_types.' + customer.billing_type.billing_type) }}</span>
                    </div>
                    <div :class="{'vat-id-check' : customer.is_vat_check_possible}">
                        <span>{{ __('customers.customers.vat_number') }}</span>
                        <span v-if="vatIdInformation.showVatNumber">
                            {{ customer.vat_number }}
                            <span
                                :class="vatIdInformation.iconClass"
                            /><br>
                            <span>
                                {{ vatIdInformation.message }}
                            </span>
                        </span>
                        <span v-if="vatIdInformation.isExempted">
                            {{ __('customers.customers.vat_number_exemption') }}
                        </span>
                        <DTag
                            v-if="vatIdInformation.isNotSet"
                            :label="__('customers.customers.no_vat_number')"
                        />
                        <span>
                            <DButton
                                v-if="customer.is_vat_check_possible"
                                id="vat-id-check"
                                mini
                                :label="__('customers.customers.vat_number_check')"
                                color="primary"
                                :disabled="!userPermissions.includes('customer.edit')"
                                @click="openCheckVatIdModal"
                            />
                        </span>
                    </div>
                    <div>
                        <span>{{ __('customers.customers.tax_exemption') }}</span>
                        <span v-if="customer.tax_exemption">{{ customer.tax_exemption.name }}</span>
                        <DTag
                            v-else
                            :label="__('customers.customers.no_tax_exemption')"
                        />
                    </div>
                    <div v-if="isFinanceSystemNetsuite">
                        <span>{{ __('customers.customers.finance_system_debtor_id') }}</span>
                        <span>{{ hasFinanceDebtorId() ? netsuiteCrmId : '' }}</span>
                    </div>
                    <div>
                        <span>
                            {{ isFinanceSystemNetsuite ? __('customers.customers.netsuite_id') : __('customers.customers.finance_system_debtor_id') }}
                        </span>
                        <span>{{ customer.finance_system_debtor_id }}</span>
                    </div>
                    <div>
                        <span>{{ __('customers.customers.postal_delivery') }}</span>
                        {{ customer.invoiceDeliveryType }}
                    </div>
                    <div>
                        <span>{{ __('customers.customers.invoice_csv') }}</span>
                        <DTag
                            v-if="customer.invoice_csv"
                            :label="__('customers.customers.active')"
                        />
                        <DTag
                            v-else
                            :label="__('customers.customers.inactive')"
                        />
                    </div>
                    <div>
                        <span>{{ __('customers.customers.customer_reference_number') }}</span>
                        <span v-if="customer.customer_reference_number">{{ customer.customer_reference_number }}</span>
                        <DTag
                            v-else
                            :label="__('messages.not_set')"
                        />
                    </div>
                    <div
                        v-if="tenantSettings.online_service_fee"
                        class="service-fee"
                    >
                        <span>{{ __('customers.customers.service_fee_date') }}</span>
                        <span>{{ setDateFormat(customer.service_fee_date) }} {{ customer.suppress_service_fee !== null ? __('customers.customers.suppressed_until', {date: setDateFormat(customer.suppress_service_fee)}) : '' }}</span>
                        <span>
                            <DButton
                                v-if="customer.is_service_fee_reset_available && userPermissions.includes('customer.edit')"
                                id="reset-last-service-fee"
                                mini
                                :label="__('customers.customers.reset_last_service_fee')"
                                color="primary"
                                @click="resetLastServiceFee"
                            />
                        </span>
                    </div>
                </div>
            </DCard>
            <DCard padding>
                <div class="detail">
                    <div>
                        <span>{{ __('customers.customers.birthday') }}</span>
                        <span v-if="customer.birthday">{{ setDateFormat(customer.birthday) }}</span>
                        <DTag
                            v-else
                            :label="__('messages.not_set')"
                        />
                    </div>
                    <div v-if="customer.customer_type === 'geschäftlich' && tenantSettings?.ssn_business_id === true">
                        <span>{{ __('customers.customers.business_id') }}</span>
                        <span v-if="customer.business_id">
                            {{ customer.business_id }}
                        </span>
                        <DTag
                            v-else
                            :label="__('messages.not_set')"
                        />
                    </div>
                    <div v-if="customer.customer_type === 'privat' && tenantSettings?.ssn_business_id === true">
                        <span>{{ __('customers.customers.ssn') }}</span>
                        <span v-if="customer.ssn">
                            {{ customer.ssn }}
                        </span>
                        <DTag
                            v-else
                            :label="__('messages.not_set')"
                        />
                    </div>

                    <div v-if="customer.customer_type === 'geschäftlich' && tenantSettings?.norway_id === true">
                        <span>{{ __('customers.customers.business_id_norway') }}</span>
                        <span v-if="customer.business_id_norway">
                            {{ customer.business_id_norway }}
                        </span>
                        <DTag
                            v-else
                            :label="__('messages.not_set')"
                        />
                    </div>
                    <div v-if="customer.customer_type === 'privat' && tenantSettings?.norway_id === true">
                        <span>{{ __('customers.customers.personal_id_norway') }}</span>
                        <span v-if="customer.personal_id_norway">
                            {{ customer.personal_id_norway }}
                        </span>
                        <DTag
                            v-else
                            :label="__('messages.not_set')"
                        />
                    </div>
                    <div v-if="customer.customer_type === 'geschäftlich' && tenantSettings?.enable_commercial_register_number === true">
                        <span>{{ __('customers.customers.commercial_register_number') }}</span>
                        <span v-if="customer.commercial_register_number">
                            {{ customer.commercial_register_number }}
                        </span>
                        <DTag
                            v-else
                            :label="__('messages.not_set')"
                        />
                    </div>
                </div>
            </DCard>
            <DCard
                :title="__('customers.customers.communication_preferences')"
                padding
            >
                <div class="detail">
                    <div
                        v-for="communicationSubscription in communicationSubscriptions"
                        :key="communicationSubscription.type"
                    >
                        <div>
                            <span>{{ __('customers.customers.newsletter_' + communicationSubscription.type) }}</span>
                        </div>
                        <div class="d-flex align-items-center gap-2">
                            <DTag
                                :label="__('customers.customers.' + communicationSubscription.status)"
                            />
                            <div>
                                {{ setDateFormat(communicationSubscription.approved_at) }}
                            </div>
                        </div>
                    </div>
                    <div>
                        <span>{{ __('customers.common.telemarketing') }}</span>
                        <DTag
                            v-if="customer.telemarketing"
                            :label="__('customers.common.telemarketing_allowed')"
                        />
                        <DTag
                            v-else
                            :label="__('customers.common.telemarketing_not_allowed')"
                        />
                    </div>
                    <div v-if="tenantSettings.send_payment_confirmation_email === true">
                        <span>{{ __('customers.common.payment_confirmation.title') }}</span>
                        <DTag
                            v-if="customer.send_payment_confirmation_email"
                            :label="__('customers.common.payment_confirmation.active')"
                        />
                        <DTag
                            v-else
                            :label="__('customers.common.payment_confirmation.inactive')"
                        />
                    </div>
                </div>
            </DCard>
        </main>
        <main>
            <DCard
                :title="__('customers.customers.quicklinks')"
                padding
            >
                <template #default>
                    <div class="quicklinks">
                        <span class="pr-1">
                            <Link
                                v-if="userPermissions.includes('task.manage')"
                                :href="tasksLink()"
                            >
                                {{ __('customers.customers.tasks') }} ({{ openTasksCount }})
                            </Link>
                            <span v-else>
                                {{ __('customers.customers.tasks') }} ({{ openTasksCount }})
                            </span>
                        </span>
                        <span class="pr-1">
                            <Link
                                v-if="userPermissions.includes('contract.manage_terminations')"
                                :href="terminationsLink()"
                            >
                                {{ __('customers.customers.terminations') }} ({{ terminationsCount }})
                            </Link>
                            <span v-else>
                                {{ __('customers.customers.terminations') }} ({{ terminationsCount }})
                            </span>
                        </span>
                        <span class="pr-1">
                            <Link
                                v-if="userPermissions.includes('contract.fulfillment')"
                                :href="fulfillmentLink()"
                            >
                                {{ __('customers.customers.fulfillment') }} ({{ fulfillmentCount }})
                            </Link>
                            <span v-else>
                                {{ __('customers.customers.fulfillment') }} ({{ fulfillmentCount }})
                            </span>
                        </span>
                        <span class="pr-1">
                            <Link
                                v-if="userPermissions.includes('contract.pending')"
                                :href="pendingContractsLink()"
                            >
                                {{ __('customers.customers.pending_contracts') }} ({{ pendingContractsCount }})
                            </Link>
                            <span v-else>
                                {{ __('customers.customers.pending_contracts') }} ({{ pendingContractsCount }})
                            </span>
                        </span>
                        <span
                            v-if="ccPaymentsCount"
                            class="pr-1"
                        >
                            <Link
                                v-if="userPermissions.includes('payment.credit_card')"
                                :href="ccPaymentsLink()"
                            >
                                {{ __('customers.customers.cc_payments') }} ({{ ccPaymentsCount }})
                            </Link>
                            <span v-else>
                                {{ __('customers.customers.cc_payments') }} ({{ ccPaymentsCount }})
                            </span>
                        </span>
                    </div>
                </template>
            </DCard>

            <DCard
                :title="__('customers.customers.creditworthiness.title')"
                padding
                footer-padding
            >
                <template #default>
                    <div class="creditworthiness">
                        <div class="row">
                            <div class="col-6">
                                <h6>{{ __('customers.customers.creditworthiness.headline') }}</h6>
                                <div class="dunning-list">
                                    <div>
                                        <span>1.</span>
                                        <span>{{ __('customers.customers.creditworthiness.dunning_list.first_reminder') }}</span>
                                        <span :class="creditworthiness.dunning_list.first_reminder.total > 0 ? 'warning' : ''">
                                            {{ creditworthiness.dunning_list.first_reminder.total }}
                                        </span>
                                    </div>
                                    <div>
                                        <span>2.</span>
                                        <span>{{ __('customers.customers.creditworthiness.dunning_list.second_reminder') }}</span>
                                        <span :class="creditworthiness.dunning_list.second_reminder.total > 0 ? 'warning' : ''">
                                            {{ creditworthiness.dunning_list.second_reminder.total }}
                                        </span>
                                    </div>
                                    <div>
                                        <span>3.</span>
                                        <span>{{ __('customers.customers.creditworthiness.dunning_list.last_reminder') }}</span>
                                        <span :class="creditworthiness.dunning_list.last_reminder.total > 0 ? 'warning' : ''">
                                            {{ creditworthiness.dunning_list.last_reminder.total }}
                                        </span>
                                    </div>
                                    <div>
                                        <span>4.</span>
                                        <span>{{ __('customers.customers.creditworthiness.dunning_list.termination') }}</span>
                                        <span :class="creditworthiness.dunning_list.termination.total > 0 ? 'warning' : ''">
                                            {{ creditworthiness.dunning_list.termination.total }}
                                        </span>
                                    </div>
                                    <div>
                                        <span>I</span>
                                        <span>{{ __('customers.customers.creditworthiness.dunning_list.collection') }}</span>
                                        <span :class="creditworthiness.dunning_list.collection.total > 0 ? 'warning' : ''">
                                            {{ creditworthiness.dunning_list.collection.total }}
                                        </span>
                                    </div>
                                    <div>
                                        <span>K</span>
                                        <span>{{ __('customers.customers.creditworthiness.dunning_list.lawsuit') }}</span>
                                        <span :class="creditworthiness.dunning_list.lawsuit.total > 0 ? 'warning' : ''">
                                            {{ creditworthiness.dunning_list.lawsuit.total }}
                                        </span>
                                    </div>
                                    <div>
                                        <span>F</span>
                                        <span>{{ __('customers.customers.creditworthiness.dunning_list.debt_loss') }}</span>
                                        <span :class="creditworthiness.dunning_list.debt_loss.total > 0 ? 'warning' : ''">
                                            {{ creditworthiness.dunning_list.debt_loss.total }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div class="col-6">
                                <div
                                    class=" mb-3 h5 pb-2 mb-4 border-bottom text-center"
                                    :class="customer.sanity === 'solvent' ? 'border-success text-success' : 'border-danger text-danger'"
                                >
                                    {{ __('customers.common.sanity.' + customer.sanity) }}
                                </div>
                                <div class="order-blocked mb-3">
                                    <span>{{ __('customers.customers.orders_blocked') }}</span>
                                    <span v-if="customer.orders_blocked">
                                        {{ __('messages.yes') }}
                                    </span>
                                    <span v-else>
                                        {{ __('messages.no') }}
                                    </span>
                                </div>
                                <div class="dunning-track">
                                    <div class="arrow-bottom-wrapper">
                                        <template v-if="creditworthiness.highest_dunning_level_last_year > 0">
                                            <span
                                                v-for="index in creditworthiness.highest_dunning_level_last_year - 1"
                                                :key="`up-${index}`"
                                            />
                                            <span class="bottom-arrow">1 {{ __('messages.units.year') }}</span>
                                        </template>
                                    </div>
                                    <div class="graph">
                                        <span>1.</span>
                                        <span>2.</span>
                                        <span>3.</span>
                                        <span>4.</span>
                                        <span>I</span>
                                        <span>K</span>
                                        <span>F</span>
                                    </div>
                                    <div class="arrow-up-wrapper">
                                        <template v-if="creditworthiness.highest_dunning_level_last_three_years > 0">
                                            <span
                                                v-for="index in creditworthiness.highest_dunning_level_last_three_years - 1"
                                                :key="`down-${index}`"
                                            />
                                            <span class="up-arrow">3 {{ __('messages.units.years') }}</span>
                                        </template>
                                    </div>
                                </div>
                                <TerminateContractsButton
                                    v-if="userPermissions.includes('contract.terminate') && customer.sanity === 'bankruptcy'"
                                    :customer="customer"
                                    :classes="'float-end mt-2'"
                                />
                            </div>
                        </div>
                    </div>
                </template>
            </DCard>

            <DCard
                :title="__('customers.customers.sticky_notes.title')"
                footer-padding
            >
                <template #default>
                    <div
                        v-for="stickyNote in stickyNotes"
                        :key="stickyNote.id"
                        class="sticky-note"
                    >
                        <div class="header">
                            <span>{{ stickyNote.userName }}</span>
                            <span>{{ setDateFormat(stickyNote.createdAt, 'YYYY-MM-DD hh:mm') }}</span>
                        </div>
                        <div class="body">
                            <div>
                                <NoteContent :note-parts="stickyNote.croppedNote" />
                                <span v-if="noteIsCropped(stickyNote)">
                                    <DButton
                                        mini
                                        icon="glasses"
                                        @click="showFullNote(stickyNote.note)"
                                    />
                                </span>
                            </div>
                            <DButton
                                v-if="userPermissions.includes('customer.edit')"
                                mini
                                icon="pencil"
                                class="btn-height"
                                @click="$inertia.get($route('customers.customers.customer-notes.edit', {
                                    customer: customer.id,
                                    customer_note: stickyNote.id
                                }))"
                            />
                        </div>
                    </div>
                </template>

                <template #footer>
                    <DButton
                        v-if="userPermissions.includes('customer.comment') || userPermissions.includes('customer.edit')"
                        mini
                        :label="__('customers.customers.sticky_notes.footer.create_note')"
                        @click="$inertia.get($route('customers.customers.customer-notes.create', {customer: customer.id}))"
                    />
                    <DButton
                        v-if="userPermissions.includes('customer.view')"
                        mini
                        :label="__('customers.customers.sticky_notes.footer.all_notes', {count: allNotesCount})"
                        @click="$inertia.get($route('customers.customers.customer-notes.index', {customer: customer.id}))"
                    />
                </template>
            </DCard>

            <DCard
                v-if="customer.direct_marketing"
                :title="__('customers.customers.sales.title')"
                padding
            >
                <div
                    v-if="customer.primary_owner"
                >
                    <div class="my-1">
                        <div class="row justify-content-around">
                            <div class="col-md-12">
                                <strong>{{ __('customers.customers.sales.primary_owner_title') }}: </strong>{{ salesPersonOutput(customer.primary_owner) }}
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-if="customer.proactive_sales_agent"
                >
                    <div class="my-1">
                        <div class="row justify-content-around">
                            <div class="col-md-12">
                                <strong>{{ __('customers.customers.sales.proactive_sales_agent_title') }}: </strong>{{ salesPersonOutput(customer.proactive_sales_agent) }}
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-if="customer.primary_owner === null && customer.proactive_sales_agent === null"
                    class="no-files"
                >
                    {{ __('customers.customers.sales.no_sales_person') }}
                </div>
            </DCard>

            <DCard
                :title="__('customers.customers.dpaDocuments.title')"
                padding
                footer-padding
            >
                <div class="dpa-document">
                    <div v-if="dpaDocuments.length === 0">
                        <DTag
                            color="danger"
                            :label="__('customers.customers.dpaDocuments.not_signed')"
                        />
                    </div>
                    <div
                        v-for="(dpa, dpaIndex) in dpaDocuments"
                        v-else
                        :key="dpaIndex"
                        class="file"
                    >
                        <DIcon
                            name="file"
                            :size="32"
                        />
                        <div class="meta">
                            <div class="filename">
                                {{ __('customers.customers.dpaDocuments.version', {version: dpa.version}) }}
                            </div>
                            <div class="description">
                                {{ __('customers.customers.dpaDocuments.signed_at', {signed_at: setDateFormat(dpa.signed_at)}) }}
                            </div>
                        </div>
                        <DButton
                            icon="download"
                            :disabled="isInLoadingState"
                            @click="downloadDpaFile(dpa.id)"
                        />
                    </div>
                </div>
                <template #footer>
                    <DButton
                        v-if="userPermissions.includes('customer.comment') || userPermissions.includes('customer.edit')"
                        mini
                        :label="__('customers.customers.dpaDocuments.upload')"
                        @click="$inertia.get($route('customers.customers.upload-dpa.create', {customer: customer.id}))"
                    />
                </template>
            </DCard>

            <DCard
                :title="__('customers.customers.files.title')"
                footer-padding
            >
                <template #default>
                    <div
                        v-for="customerFile in customerFiles"
                        :key="customerFile.id"
                        class="file"
                    >
                        <DIcon
                            name="file"
                            :size="32"
                        />
                        <div class="meta">
                            <div class="filename">
                                {{ customerFile.original_name }}
                            </div>
                            <div class="description">
                                {{ customerFile.description }}
                            </div>
                            <div class="meta">
                                {{ setDateFormat(customerFile.created_at) }} &bull; {{ customerFile.user_name }}
                            </div>
                        </div>
                        <DButton
                            icon="download"
                            :disabled="isInLoadingState"
                            @click="customerFile.nitro_file === true ? downloadFile(customerFile.id) : downloadWeSellFile(customerFile)"
                        />
                    </div>
                    <div
                        v-if="customerFiles.length === 0"
                        class="no-files"
                    >
                        {{ __('customers.customers.files.no_files') }}
                    </div>
                </template>

                <template #footer>
                    <DButton
                        v-if="userPermissions.includes('customer.comment') || userPermissions.includes('customer.edit')"
                        mini
                        :label="__('customers.customers.files.footer.upload_file')"
                        @click="$inertia.get($route('customers.customers.customer-files.create', {customer: customer.id}))"
                    />
                    <DButton
                        v-if="userPermissions.includes('customer.view')"
                        mini
                        :label="__('customers.customers.files.footer.all_files')"
                        @click="$inertia.get($route('customers.customers.customer-files.index', {customer: customer.id}))"
                    />
                </template>
            </DCard>
            <DCard
                :title="__('customers.customers.transfers.title')"
                padding
            >
                <div
                    v-if="customer.transfers.length > 0"
                >
                    <div
                        v-for="transfer in customer.transfers"
                        :key="transfer.id"
                        class="my-1"
                    >
                        <div class="row justify-content-around">
                            <div class="col-md-4">
                                <Link :href="contractTransferLink(transfer)">
                                    ID: {{ transfer.id }}
                                </Link>
                            </div>
                            <div class="col-md-4">
                                Status: {{ __('customers.customers.transfers.statuses.' + transfer.status) }}
                            </div>
                            <div class="col-md-4 button-div">
                                <DButton
                                    v-if="transfer.status === 'open' && userPermissions.includes('contract.transfer.cancel')"
                                    class="p-2 mb-1"
                                    :label="__('customers.customers.transfers.buttons.cancel_transfer')"
                                    @click="cancelTransfer(transfer)"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-else
                    class="no-files"
                >
                    {{ __('customers.customers.transfers.no_transfers') }}
                </div>
            </DCard>
        </main>

        <DFabs>
            <s-s-o-fab
                :customer="customer"
                @open-modal="showCreateCloudpitCustomerModal = true"
                @close-modal="showCreateCloudpitCustomerModal = false"
            />
            <DFab
                v-if="userPermissions.includes('customer.edit')"
                id="btn-edit"
                icon="pencil"
                :label="__('messages.actions.edit')"
                @click="$inertia.get($route('customers.customers.edit', customer.id))"
            />
        </DFabs>

        <DModal
            v-model:show="showNoteModal"
            is-big
            :title="__('customers.customers.sticky_notes.title')"
            @close="showNoteModal = false"
        >
            <template #default>
                <NoteContent :note-parts="selectedNote" />
            </template>
        </DModal>
        <ResetPasswordModal
            :show-modal="showPasswordResetModal"
            @confirm="resetPassword"
            @close="showPasswordResetModal=false"
        />
        <Disable2FAModal
            :show-modal="showDisable2FAModal"
            @confirm="disable2Fa"
            @close="showDisable2FAModal=false"
        />
        <DModal
            v-model:show="showTransferCancellationModal"
            :title="__('contracts.transfers.delete_modal.title')"
            :message="__('contracts.transfers.delete_modal.body')"
            is-confirm
            is-error
            @close="showTransferCancellationModal = false"
            @confirm="cancelCallBack"
        />
        <CreateCloudPitCustomerModal
            :customer="customer"
            :show-modal="showCreateCloudpitCustomerModal"
            @close-modal="showCreateCloudpitCustomerModal = false"
        />
        <VatIdCheckModal
            :customer="customer"
            :show-modal="showVatIdCheckModal"
            @close-modal="showVatIdCheckModal = false"
        />
        <SendOnetimeLoginModal
            :customer="customer"
            :users="cloudPitUsers"
            :show="showOnetimeLoginModal"
            @close-modal="showOnetimeLoginModal = false; cloudPitUsers = []"
            @axios-message="setAxiosMessage"
        />
        <SwitchControlPanelModal
            v-if="showSwitchPanelModal"
            :show="showSwitchPanelModal"
            :customer="customer"
            @axios-message="setAxiosMessage"
            @close-modal="showSwitchPanelModal = false"
        />
    </div>
</template>

<script>

import CustomerBase from '~@/Pages/Customers/Base'
import App from '~@/App.vue'
import SSOFab from '~@/Components/Customers/SSOFab'
import CreateCloudPitCustomerModal from '~@/Components/Customers/CreateCloudPitCustomerModal'
import NoteContent from '~@/Components/Customers/NoteContent'
import VatIdCheckModal from '~@/Components/Customers/Customers/Modals/VatIdCheck.vue'
import ResetPasswordModal from '~@/Components/Customers/ResetPasswordConfirmationModal.vue'
import Disable2FAModal from '~@/Components/Customers/Disable2FAConfirmationModal.vue'
import SendOnetimeLoginModal from '~@/Components/Customers/SendOneTimeLoginModal.vue'
import SwitchControlPanelModal from '../../../Components/Customers/SwitchControlPanelModal.vue'
import IncidentFlag from '~@/Components/Customers/BusinessIncidents/IncidentFlag'
import TerminateContractsButton from '~@/Components/Customers/Contracts/TerminateContractsButton.vue'

export default {
    name: 'CustomerProfile',
    components: {
        TerminateContractsButton,
        SwitchControlPanelModal,
        ResetPasswordModal,
        NoteContent,
        CreateCloudPitCustomerModal,
        SSOFab,
        VatIdCheckModal,
        Disable2FAModal,
        SendOnetimeLoginModal,
        IncidentFlag
    },
    layout: [App, CustomerBase],
    props: {
        customer: {
            type: Object,
            default: () => { return {} }
        },
        creditworthiness: {
            type: Object,
            default: () => { return {} }
        },
        stickyNotes: {
            type: Array,
            default: () => { return [] }
        },
        allNotesCount: {
            type: Number,
            default: () => { return 0 }
        },
        dpaDocuments: {
            type: Array,
            default: () => { return [] }
        },
        customerFiles: {
            type: Array,
            default: () => { return [] }
        },
        openTasksCount: {
            type: Number,
            default: () => { return 0 }
        },
        terminationsCount: {
            type: Number,
            default: () => { return 0 }
        },
        fulfillmentCount: {
            type: Number,
            default: () => { return 0 }
        },
        pendingContractsCount: {
            type: Number,
            default: () => { return 0 }
        },
        ccPaymentsCount: {
            type: Number,
            default: () => { return 0 }
        },
        newsletterSettings: {
            type: Object,
            default: () => { return {} }
        },
        useHeroldLocationIdOnCustomer: {
            type: Boolean,
            default: () => { return false }
        },
        customerLocation: {
            type: String,
            default: ''
        },
        isOneHomeAccountCreationPending: {
            type: Boolean,
            default: () => { return false }
        },
        isInvoiceBeforeRenewalEnabled: {
            type: Boolean,
            default: false
        },
        communicationSubscriptions: {
            type: Array,
            default: () => { return [] }
        },
        isFinanceSystemNetsuite: {
            type: Boolean,
            default: false
        },
        netsuiteCrmId: {
            type: String,
            default: ''
        },
    },
    data () {
        return {
            showNoteModal: false,
            showCreateCloudpitCustomerModal: false,
            selectedNote: '',
            showTransferCancellationModal: false,
            cancelCallBack: () => { return {} },
            showVatIdCheckModal: false,
            showPasswordResetModal: false,
            showDisable2FAModal: false,
            showOnetimeLoginModal: false,
            showSwitchPanelModal: false,
            cloudPitUsers: [],
            axiosMessages: {
                message: '',
                success: null,
                showMessage: false
            }
        }
    },
    computed: {
        creditCardPaymentMethod () {
            return this.customer.payment_method === 'credit_card'
        },
        segments () {
            if (!this.customer || !this.customer.customer_segments || this.customer.customer_segments.length < 1) return []

            return this.customer.customer_segments
        },
        sepaPaymentMethod () {
            return (this.customer.payment_method === 'sepa' || this.customer.payment_method === 'sepa-b2b') && this.customer.active_sepa_mandate
        },
        shouldShowSepa () {
            return (this.sepaPaymentMethod && this.tenantSettings.enable_divergent_payment_methods === false) ||
               (this.tenantSettings.enable_divergent_payment_methods === true && this.customer.active_sepa_mandate?.id !== null)
        },
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        vatIdInformation () {
            let message
            let iconClass
            if (
                this.customer.vat_number &&
                this.customer.vat_number_checked_at &&
                this.customer.vat_number_valid
            ) {
                message = this.__(
                    'customers.customers.vat_number_messages.is_checked',
                    { checkedAt: this.setDateFormat(this.customer.vat_number_checked_at, 'DD. MMM YYYY') }
                )
                iconClass = 'mdi mdi-check-circle text-success'
            }

            if (
                this.customer.vat_number &&
                !this.customer.vat_number_checked_at &&
                !this.customer.vat_number_valid
            ) {
                message = this.__('customers.customers.vat_number_messages.is_not_checked')
                iconClass = 'mdi mdi-help-circle text-warning'
            }

            if (
                this.customer.vat_number &&
                this.customer.vat_number_checked_at &&
                !this.customer.vat_number_valid
            ) {
                message = this.__(
                    'customers.customers.vat_number_messages.is_checked',
                    { checkedAt: this.setDateFormat(this.customer.vat_number_checked_at, 'DD. MMM YYYY') }
                )
                iconClass = 'mdi mdi-close-circle text-danger'
            }

            return {
                showVatNumber: this.customer.vat_number && !this.customer.vat_number_exemption,
                isExempted: this.customer.vat_number_exemption,
                isNotSet: !this.customer.vat_number && !this.customer.vat_number_exemption,
                iconClass: iconClass,
                message: message
            }
        },
        billingVatIdInformation () {
            let message
            let iconClass
            if (
                this.customer.invoice_vat_number &&
                this.customer.invoice_vat_number_checked_at &&
                this.customer.invoice_vat_number_valid
            ) {
                message = this.__(
                    'customers.customers.vat_number_messages.is_checked',
                    { checkedAt: this.setDateFormat(this.customer.invoice_vat_number_checked_at, 'DD. MMM YYYY') }
                )
                iconClass = 'mdi mdi-check-circle text-success'
            }

            if (
                this.customer.invoice_vat_number &&
                !this.customer.invoice_vat_number_checked_at &&
                !this.customer.invoice_vat_number_valid
            ) {
                message = this.__('customers.customers.vat_number_messages.is_not_checked')
                iconClass = 'mdi mdi-help-circle text-warning'
            }

            if (
                this.customer.invoice_vat_number &&
                this.customer.invoice_vat_number_checked_at &&
                !this.customer.invoice_vat_number_valid
            ) {
                message = this.__(
                    'customers.customers.vat_number_messages.is_checked',
                    { checkedAt: this.setDateFormat(this.customer.invoice_vat_number_checked_at, 'DD. MMM YYYY') }
                )
                iconClass = 'mdi mdi-close-circle text-danger'
            }

            return {
                showVatNumber: this.customer.invoice_vat_number && !this.customer.invoice_vat_number_exemption,
                isExempted: this.customer.invoice_vat_number_exemption,
                isNotSet: !this.customer.invoice_vat_number && !this.customer.invoice_vat_number_exemption,
                iconClass: iconClass,
                message: message
            }
        },
        reminderStopUntilDateInPast () {
            return this.customer.dunning_block_until < new Date().toISOString().split('T')[0]
        },
        tenantSettings () {
            return this.$page.props.tenantSettings
        }
    },
    methods: {
        downloadFile (fileId) {
            window.open(this.$route('customers.customers.customer-files.download', {
                customer: this.customer.id,
                customerFile: fileId
            }), '_blank')
        },
        downloadWeSellFile (file) {
            window.open(file.url, '_blank')
        },
        downloadDpaFile (fileId) {
            window.open(this.$route('customers.customers.customer-dpa.download', {
                customer: this.customer.id,
                customerDpaDocument: fileId
            }), '_blank')
        },
        showFullNote (note) {
            this.selectedNote = note
            this.showNoteModal = true
        },
        noteIsCropped (note) {
            const noteContent = Object.values(note.note.texts).join(' ') + Object.values(note.note.links).join(' ')
            const croppedNoteContent = Object.values(note.croppedNote.texts).join(' ') + Object.values(note.croppedNote.links).join(' ')

            return noteContent.length > croppedNoteContent.length
        },
        tasksLink () {
            const filter = {
                statuses: null,
                types: null,
                search: this.customer.id,
                brands: null,
                customer_segments: null
            }

            return this.$route('tasks.tasks.index', { _query: filter })
        },
        terminationsLink () {
            return this.$route('contracts.terminations', { search: this.customer.id, tab: 'open', brands: null })
        },
        fulfillmentLink () {
            const filter = {
                action: null,
                module: null,
                status: [
                    'failed',
                    'queued',
                    'retry',
                    'paused'
                ],
                updatedAtFrom: null,
                updatedAtTo: null,
                customer: this.customer.id,
                contract: null,
                reset: false
            }

            return this.$route('tools.fulfillment.index', { _query: filter })
        },
        pendingContractsLink () {
            const filter = {
                dateFrom: null,
                dateTo: null,
                productName: null,
                customer: this.customer.id
            }

            return this.$route('contracts.pending', { _query: filter })
        },
        ccPaymentsLink () {
            const filter = {
                reference: null,
                invoiceId: null,
                transactionStatus: null,
                customerId: this.customer.id
            }

            return this.$route('tools.credit-card.transactions', { _query: filter })
        },
        contractTransferLink (transfer) {
            return this.$route('contracts.transfers', { transferId: transfer.id })
        },
        cancelTransfer (item) {
            this.cancelCallBack = () => {
                this.$inertia.patch(
                    this.$route('contracts.transfers.cancel', {
                        transfer: item.id
                    }),
                    {
                        preserveScroll: true
                    }
                )
                this.showTransferCancellationModal = false
            }
            this.showTransferCancellationModal = true
        },
        openCheckVatIdModal () {
            this.showVatIdCheckModal = true
        },
        resetPassword () {
            this.$inertia.get(
                this.$route('customers.customers.reset-cloudpit-password',
                    { customer: this.customer.id }
                )
            )
            this.showPasswordResetModal = false
        },
        disable2Fa () {
            this.$inertia.get(
                this.$route('customers.customers.deactivate-2fa',
                    { customer: this.customer.id }
                )
            )
            this.showPasswordResetModal = false
        },
        getCloudPitUsersForTheAccount () {
            window.axios.get(this.$route('customers.customers.cloudpit_get_accounts', { customer: this.customer.id }))
                .then((response) => {
                    this.cloudPitUsers = response.data.result
                    this.showOnetimeLoginModal = true
                })
                .catch((error) => {
                    this.setAxiosMessage({
                        message: error.response.data.message,
                        success: false,
                        showMessage: true
                    })
                })
        },
        setAxiosMessage (data) {
            this.axiosMessages = data
        },
        resetAxiosMessage () {
            this.axiosMessages = {
                message: '',
                success: null,
                showMessage: false
            }
        },
        showCompanyField (field) {
            return this.customer.customer_type === 'geschäftlich' ||
                (field !== null && field !== '')
        },
        copyToClipboard (value) {
            navigator.clipboard.writeText(value)
        },
        salesPersonOutput (value) {
            return `${value.firstname} ${value.lastname} (${value.email ?? ''} ${value.phone_number ?? ''}) (SM: ${value.manager_name ?? ''})`
        },
        resetLastServiceFee () {
            this.$inertia.get(
                this.$route('customers.customers.reset-last-service-fee',
                    { customer: this.customer.id }
                )
            )
        },
        hasFinanceDebtorId () {
            return typeof this.customer.finance_system_debtor_id === 'string' &&
                this.customer.finance_system_debtor_id.trim() !== ''
        }
    }
}
</script>

<style lang="scss" scoped>
.z-index-1000 {
    z-index: 10000
}
.action-buttons {
    margin-left: 10px;
    top: - 10px;
}
.copy-buttons {
    margin-left: 10px;
    margin-top: -7px;
}
.customer-profile {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 15px;

    & main {
        display: flex;
        flex-direction: column;
        grid-gap: 15px;

        & .quicklinks {
            & > span:not(:last-child)::after {
                content: ' ● ';
            }
        }

        & .creditworthiness {
            display: flex;
            flex-direction: column;
            grid-gap: 15px;
            & h6 {
                font-size: 13px;
                font-weight: bold;
            }
            & .dunning-list {
                margin-top: 14px;
                display: flex;
                flex-direction: column;
                grid-gap: 5px;
                & > div {
                    display: grid;
                    grid-template-columns: 1fr 5fr 1fr;
                    grid-gap: 10px;
                    font-weight: bold;
                    & span {
                        &.warning {
                            color: red;
                        }
                    }
                }
            }
            & .sanity {
                display: block;
                text-align: center;
                padding: 12px 4px;
                border-radius: 4px;
                background-color: red;
                color: white;
                &.success {
                    background-color: green;
                }
            }
            & .order-blocked {
                display: grid;
                grid-template-columns: 5fr 2fr;
                grid-gap: 15px;
                text-align: right;
                font-weight: bold;
                & > span:last-child {
                    padding-right: 10px;
                }
            }
            & .dunning-track {
                & > div {
                    display: grid;
                    grid-template-columns: repeat(7, 1fr);
                    text-align: center;
                    font-weight: bold;
                    grid-gap: 10px;
                    padding: 0 10px;
                }
                & .arrow-bottom-wrapper {
                    margin-bottom: 8px;
                    font-size: 11px;
                }
                & .arrow-up-wrapper {
                    margin-top: 8px;
                    font-size: 11px;
                }
                & span.bottom-arrow {
                    position: relative;
                    &:after {
                        content:'';
                        position: absolute;
                        left: 0;
                        right: 0;
                        bottom: -6px;
                        margin: 0 auto;
                        width: 0;
                        height: 0;
                        border-top: 6px solid #1e8fdf;
                        border-left: 12px solid transparent;
                        border-right: 12px solid transparent;
                    }
                }
                & span.up-arrow {
                    position: relative;
                    &:after {
                        content:'';
                        position: absolute;
                        left: 0;
                        right: 0;
                        top: -6px;
                        margin: 0 auto;
                        width: 0;
                        height: 0;
                        border-bottom: 6px solid #1e8fdf;
                        border-left: 12px solid transparent;
                        border-right: 12px solid transparent;
                    }
                }
                & .graph {
                    padding: 10px;
                    background: #27cf00;
                    background: linear-gradient(90deg, #27cf00 0%, #eef236 49%, #e24700 100%);
                }
            }
        }

        .reset-password-button {
            margin-left: 10px
        }

        & .detail {
            display: flex;
            flex-direction: column;
            grid-gap: 15px;
            & > div {
                display: grid;
                grid-template-columns: 3fr 5fr;
                grid-gap: 15px;

                &.vat-id-check {
                    grid-template-columns: 3.15fr 3fr 2fr;
                    align-items: center;
                }

                &.has-incident {
                    grid-template-columns: 3.15fr 3fr 2fr;
                    align-items: center;
                }

                &.service-fee {
                    grid-template-columns: 3.15fr 3.3fr 1.7fr;
                    align-items: center;
                }

                .extended-company {
                    font-weight: normal !important;
                }

                & span:nth-child(1) {
                    font-weight: bold;
                }

                & span:nth-child(2) {
                    line-height: 1.5em;
                    word-break: break-all;
                }

                & .segments {
                    grid-gap: 5px;
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                }
            }
        }

        & .sticky-note {
            padding: 15px;

            &:not(:last-child) {
                border-bottom: 1px solid #D9DBE0;
            }

            & .header {
                display: grid;
                grid-template-columns: 1fr auto;
                margin-bottom: 10px;

                & span:nth-child(1) {
                    font-weight: bold;
                }
            }
            & .body {
                display: grid;
                grid-template-columns: 1fr auto;
                word-break: break-all;

                & div:nth-child(1) {
                    line-height: 1.5em;
                }
                & .btn-height {
                    height: 2em;
                }
            }
        }

        & .dpa-document {
            display: block;
            grid-template-columns: 1fr auto;
            grid-gap: 15px;
            align-items: center;

            & .two-line {
                grid-gap: 10px;
                justify-content: flex-start;

                & span {
                    font-weight: bold;
                }

                & .dpa-download {
                    width: 100%;
                    justify-content: flex-end;
                }
            }
        }

        & .no-files {
            padding: 30px 15px;
            text-align: center;
            opacity: 0.6;
        }

        & .file {
            padding: 15px;
            display: grid;
            grid-template-columns: auto 1fr auto;
            grid-gap: 15px;
            align-items: center;

            & .mdi {
                opacity: 0.4;
            }

            & > .meta {
                display: grid;
                grid-gap: 10px;

                & .filename {
                    font-weight: bold;
                }
                & .description {
                    word-break: break-all;
                }
                & .meta {
                    opacity: 0.6;
                    font-size: 12px;
                }
            }

            &:not(:last-child) {
                border-bottom: 1px solid #D9DBE0;
            }
        }
    }
    & .modal-note-content {
        word-break: break-all;
        padding: 20px;
    }
    & .button-div {
        min-height: 40px;
    }
    & .mdi {
        &.text-success {
            color: #2eb85c !important;
        }
        &.text-danger {
            color: #e55353 !important
        }
        &.text-warning {
            color: #f9b115 !important;
        }
    }
}
</style>
